import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getCssWorkspaceZoom } from '../lib/displayScale';

export default function DropdownMenu({
  trigger,
  groups = [],
  header = null,
  align = 'end',
  width = 224,
  triggerClassName = 'crm-dropdown-trigger',
  menuClassName = '',
  ariaLabel = 'Open menu',
  title,
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const place = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const zoom=getCssWorkspaceZoom();
    const maxLeft = Math.max(8, window.innerWidth/zoom - width - 8);
    const preferred = align === 'start' ? rect.left/zoom : rect.right/zoom - width;
    setPosition({ top: Math.min(window.innerHeight/zoom - 12, rect.bottom/zoom + 6), left: Math.max(8, Math.min(maxLeft, preferred)) });
  };

  const toggle = event => {
    event.stopPropagation();
    if (!open) place();
    setOpen(value => !value);
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = event => {
      if (!triggerRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = event => event.key === 'Escape' && setOpen(false);
    const reposition = () => { place(); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  const choose = item => {
    if (item.disabled) return;
    setOpen(false);
    item.onSelect?.();
  };

  return <span className="crm-dropdown-root">
    <button ref={triggerRef} type="button" className={triggerClassName} aria-label={ariaLabel} title={title} aria-haspopup="menu" aria-expanded={open} onClick={toggle}>
      {trigger}
    </button>
    {open && createPortal(
      <div ref={menuRef} className={`crm-dropdown-menu ${menuClassName}`.trim()} role="menu" style={{ top: position.top, left: position.left, width }}>
        {header && <div className="crm-dropdown-header">{header}</div>}
        {groups.map((group, groupIndex) => <div className="crm-dropdown-group-wrap" key={group.key || group.label || groupIndex}>
          {(header || groupIndex > 0) && <div className="crm-dropdown-separator" />}
          {group.label && <div className="crm-dropdown-label">{group.label}</div>}
          <div className="crm-dropdown-group">
            {(group.items || []).filter(Boolean).map((item, itemIndex) => {
              const Icon = item.icon;
              const selectable = Object.prototype.hasOwnProperty.call(item, 'selected');
              return <button
                key={item.key || item.label || itemIndex}
                type="button"
                role={selectable ? 'menuitemradio' : 'menuitem'}
                aria-checked={selectable ? Boolean(item.selected) : undefined}
                disabled={item.disabled}
                className={`crm-dropdown-item ${item.danger ? 'destructive' : ''} ${item.selected ? 'selected' : ''}`.trim()}
                onClick={() => choose(item)}
              >
                {Icon && <Icon size={16} />}
                <span>{item.label}</span>
                {item.shortcut && <kbd>{item.shortcut}</kbd>}
                {item.selected && <span className="crm-dropdown-check" aria-hidden="true">✓</span>}
              </button>;
            })}
          </div>
        </div>)}
      </div>,
      document.body,
    )}
  </span>;
}
